import React from 'react';
import { Button, Input, Label } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragHandle from './DragHandle';
import DeleteIcon from './icons/DeleteIcon';
import { partial } from '../../../../util/translation/translation';

export function createOption() {
  return {
    id: crypto.randomUUID(),
    title: '',
    subtitle: '',
    capacity: 1,
  };
}

export default function ReservationOptionEditor({ options = [], onChange }) {
  const qT = partial('question');

  const handleAdd = () => {
    onChange([...options, createOption()]);
  };

  const handleDelete = (index) => {
    const updated = options.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return;
    const updated = [...options];
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);
    onChange(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = options.map((opt, i) => {
      if (i !== index) return opt;
      if (field === 'capacity') {
        const num = parseInt(value, 10);
        return { ...opt, [field]: isNaN(num) || num < 1 ? 1 : num };
      }
      return { ...opt, [field]: value };
    });
    onChange(updated);
  };

  return (
    <div className="reservation-option-editor">
      <Label className="form-label reservation-option-editor__section-label">
        {qT('options')}
      </Label>
      {options.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reservation-options">
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {options.map((option, index) => (
                  <Draggable
                    key={option.id}
                    draggableId={option.id}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        className="reservation-option-editor__item d-flex"
                      >
                        <div
                          {...draggableProvided.dragHandleProps}
                          className="draggable-handle reservation-option-editor__drag-handle"
                        >
                          <DragHandle />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex reservation-option-editor__row">
                            <div className="flex-grow-1">
                              <small className="reservation-option-editor__label">
                                {qT('reservationOptionTitle')} *
                              </small>
                              <Input
                                type="text"
                                className="reservation-option-editor__input sd-input"
                                value={option.title}
                                onChange={(e) =>
                                  handleFieldChange(
                                    index,
                                    'title',
                                    e.target.value
                                  )
                                }
                                placeholder={qT('reservationOptionTitle')}
                                required
                              />
                            </div>
                            <div
                              className="reservation-option-editor__capacity"
                              style={{ width: 90 }}
                            >
                              <small className="reservation-option-editor__label">
                                {qT('reservationOptionCapacity')} *
                              </small>
                              <Input
                                type="number"
                                className="reservation-option-editor__input sd-input"
                                value={option.capacity}
                                onChange={(e) =>
                                  handleFieldChange(
                                    index,
                                    'capacity',
                                    e.target.value
                                  )
                                }
                                min={1}
                                required
                              />
                            </div>
                          </div>
                          <div className="reservation-option-editor__row">
                            <small className="reservation-option-editor__label">
                              {qT('reservationOptionSubtitle')}
                            </small>
                            <Input
                              type="text"
                              className="reservation-option-editor__input sd-input"
                              value={option.subtitle}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  'subtitle',
                                  e.target.value
                                )
                              }
                              placeholder={qT(
                                'reservationOptionSubtitlePlaceholder'
                              )}
                            />
                          </div>
                        </div>
                        <Button
                          color="link"
                          className="p-0 text-danger reservation-option-editor__delete"
                          onClick={() => handleDelete(index)}
                        >
                          <DeleteIcon />
                        </Button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <Button
        color="link"
        className="reservation-option-editor__add sd-matrixdynamic__btn p-0"
        onClick={handleAdd}
      >
        + {qT('reservationOptionAdd')}
      </Button>
    </div>
  );
}
